import { authErrorCopy } from '@/lib/authErrorCopy';
import { useState, useEffect, useRef } from 'react';
import { parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/gtag';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { Mail, CheckCircle2, Eye, EyeOff, Clock, Fingerprint, MapPin, AlertCircle, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';
import { isPasswordStrong, PASSWORD_REQUIREMENTS_TEXT } from '@/lib/validationSchemas';
import { STATE_ELIGIBILITY } from '@/lib/eligibility/config';
import { DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';
import { safeReturnToPath } from '@/lib/safeRedirect';

const LEGAL_LINKS = {
  terms: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=0eef0aaa-538d-4434-9471-2d372eb58c22',
  privacy: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=74b4f847-6b13-45b6-9a95-fa2cd405c4a6',
  cookies: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=b68d815d-0463-455f-8bb1-1aef0ad6efd2',
};

// SAB-432: sourced from the eligibility config (single source of truth).
// Eligibility itself (fantasy_legal + minimum_age) still goes through the
// DB-backed check-eligibility edge fn — this is just the dropdown labels.
const US_STATES = Object.values(STATE_ELIGIBILITY)
  .map((s) => ({ code: s.code, name: s.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const Auth = () => {
  const { user, isLoading, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [suspendedUntil, setSuspendedUntil] = useState<string | null>(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState('Face ID');
  const [bioSigningIn, setBioSigningIn] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const { toast } = useToast();

  // Real-time state-eligibility check on State select change (MLB-003).
  // Doesn't block — surfaces inline warning as user fills the form.
  const checkStateEligibility = async (code: string) => {
    if (!code) { setEligibilityError(null); return; }
    try {
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check_state', state_code: code }),
      });
      const data = await res.json();
      if (!data.eligible) {
        setEligibilityError(data.reason || `Real-money play is not available in ${US_STATES.find(s => s.code === code)?.name || code}.`);
      } else {
        setEligibilityError(null);
      }
    } catch { /* silently ignore — submit-time check is the gate */ }
  };

  // Biometric login is disabled while we migrate off Capacitor Preferences
  // (which stores values in unencrypted UserDefaults / SharedPreferences).
  // Clean up any previously-stored plaintext credentials on mount.
  useEffect(() => {
    const cleanupBio = async () => {
      try {
        await Preferences.remove({ key: 'sf-bio-password' });
        await Preferences.remove({ key: 'sf-bio-email' });
      } catch { /* Capacitor not available on web — Preferences falls back to localStorage */ }
      localStorage.removeItem('sf-biometric-enabled');
      setBiometricAvailable(false);
    };
    cleanupBio();
  }, []);

  // (Legacy bio-detect removed — re-enable with secure storage in SAB-232.)

  // Track whether we detected a recovery token — persists across re-renders
  const isRecoveryFlow = useRef(
    window.location.hash.includes('type=recovery')
  );

  // Detect password recovery tokens in the URL hash and redirect to reset page
  useEffect(() => {
    if (isRecoveryFlow.current) {
      navigate('/reset-password' + window.location.hash, { replace: true });
    }
  }, [navigate]);

  // If this is a recovery flow, don't render anything — we're redirecting
  if (isRecoveryFlow.current) {
    return null;
  }

  // SAB-642: MLB invites were shared as /auth?invite=CODE (MembersTab), but this page only
  // read ?returnTo=, so the invite was dropped and the invitee fell through to /home -> create
  // — silently breaking the links already in our 44 beta users' hands. Route ?invite=CODE to
  // the public MLB join page, which carries the invite through sign-up + email confirmation via
  // the same returnTo path NFL uses. Unconditional on auth state; the target handles both.
  const inviteParam = new URLSearchParams(location.search).get('invite');
  if (inviteParam) {
    const normalizedInvite = inviteParam.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (normalizedInvite) return <Navigate to={`/beta/join/${normalizedInvite}`} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    // SAB-592 + SAB-587: a valid returnTo (an invite link routed through /auth) wins,
    // so an invitee who reaches this page signed-in still lands on the invite. Else
    // the originally intended deep-link (location.state.from). Else DEFAULT_AUTHED_HOME
    // = the /home landing gate, which resolves the SAB-587 chain (active NFL ->
    // in_season MLB -> any NFL -> create/join). Priority: returnTo -> from -> gate.
    const returnTo = new URLSearchParams(location.search).get('returnTo');
    const intendedPath = safeReturnToPath(returnTo)
      ?? ((location.state as { from?: string })?.from || DEFAULT_AUTHED_HOME);
    return <Navigate to={intendedPath} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setPasswordMismatch(false);
    setAuthError(null);

    try {
      if (isSignUp) {
        // Validate confirm password
        if (password !== confirmPassword) {
          setPasswordMismatch(true);
          setSubmitting(false);
          return;
        }

        if (!isPasswordStrong(password)) {
          toast({ title: 'Password too weak', description: PASSWORD_REQUIREMENTS_TEXT, variant: 'destructive' });
          setSubmitting(false);
          return;
        }

        const trimmedUsername = username.trim().toLowerCase();

        if (!trimmedUsername) {
          toast({ title: 'Missing fields', description: 'Username is required.', variant: 'destructive' });
          return;
        }

        if (!/^[a-z0-9_]{3,20}$/.test(trimmedUsername)) {
          toast({ title: 'Invalid username', description: 'Username must be 3-20 characters, lowercase letters, numbers, and underscores only.', variant: 'destructive' });
          return;
        }

        // Eligibility check: state + age
        if (!stateCode) {
          toast({ title: 'State required', description: 'Please select your state of residence.', variant: 'destructive' });
          return;
        }
        if (!dateOfBirth) {
          toast({ title: 'Date of birth required', description: 'Please enter your date of birth.', variant: 'destructive' });
          return;
        }

        // Check state eligibility + age via edge function
        try {
          const eligRes = await fetch(
            `${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'check_state', state_code: stateCode }),
            }
          );
          const eligData = await eligRes.json();

          if (!eligData.eligible) {
            setEligibilityError(eligData.reason || 'Real-money fantasy sports is not available in your state.');
            setSubmitting(false);
            return;
          }

          // Check age against state minimum
          // SAB-412: parseISO — dateOfBirth is a bare YYYY-MM-DD from the
          // date input. Bare `new Date(...)` parses as UTC midnight and could
          // shift the year/month/day for users west of UTC (notably born-on-
          // Jan-1 cases where the year reads as the prior year, inflating age
          // by 1).
          const birth = parseISO(dateOfBirth);
          const today = new Date();
          let age = today.getFullYear() - birth.getFullYear();
          const monthDiff = today.getMonth() - birth.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;

          if (age < eligData.minimum_age) {
            setEligibilityError(`You must be ${eligData.minimum_age}+ to play real-money fantasy sports in ${eligData.state_name}.`);
            setSubmitting(false);
            return;
          }

          setEligibilityError(null);
        } catch {
          // Fail open — don't block registration if eligibility service is down
          console.error('Eligibility check failed, proceeding with registration');
        }

        const { data: usernameAvailable } = await supabase.rpc('check_name_available', {
          check_type: 'username',
          check_value: trimmedUsername,
        });

        if (usernameAvailable === false) {
          toast({ title: 'Username taken', description: 'That username is already in use.', variant: 'destructive' });
          return;
        }

        const { error } = await signUp(email, password, trimmedUsername);
        if (error) {
          // SAB-985: classified copy, never the raw message -- it carried our Supabase
          // hostname into a user-facing toast. The raw text goes to the console only.
          const c = authErrorCopy(error, 'signup');
          console.error('[auth] sign-up failed:', c.detail);
          setAuthError(c.description);
          toast({ title: c.title, description: c.description, variant: 'destructive' });
        } else {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            await supabase.from('profiles').update({ username: trimmedUsername, display_name: trimmedUsername }).eq('id', session.user.id);
            // SAB-500: the eligibility columns (date_of_birth, state_code,
            // age_verified) are server-authoritative and no longer client-writable
            // (protect_rg_eligibility_columns trigger). Route through the
            // set_eligibility SECURITY DEFINER RPC, which derives the user from
            // auth.uid() and computes age_verified server-side -- the same
            // authoritative path /verify-eligibility uses. Non-fatal: if it does
            // not complete, the user can finish later via /verify-eligibility, so
            // signup is never blocked by it.
            if (dateOfBirth && stateCode) {
              try {
                await supabase.rpc('set_eligibility', {
                  p_date_of_birth: dateOfBirth,
                  p_state_code: stateCode,
                });
              } catch (e) {
                console.warn('[signup] set_eligibility failed (non-fatal):', e);
              }
            }
          }
          trackEvent('sign_up');
          setConfirmationSent(true);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          // SAB-985: this is the reported toast. "Load failed (…supabase.co)" was the raw
          // WebKit fetch error plus our project hostname, and it read identically to a
          // wrong password -- so the user could not tell which to fix.
          const c = authErrorCopy(error, 'signin');
          console.error('[auth] sign-in failed:', c.detail);
          setAuthError(c.description);
          toast({ title: c.title, description: c.description, variant: 'destructive' });
        } else {
          const { data: { session: loginSession } } = await supabase.auth.getSession();
          if (loginSession?.user) {
            // Check for active suspension
            const { data: suspensions } = await supabase
              .from('account_suspensions')
              .select('suspended_until')
              .eq('user_id', loginSession.user.id)
              .gt('suspended_until', new Date().toISOString())
              .order('suspended_until', { ascending: false })
              .limit(1);

            if (suspensions && suspensions.length > 0) {
              setSuspendedUntil(suspensions[0].suspended_until);
              await supabase.auth.signOut();
              return;
            }

            const { data: profile } = await supabase
              .from('profiles')
              .select('username, display_name')
              .eq('id', loginSession.user.id)
              .maybeSingle();
            if (profile && !profile.username && !profile.display_name) {
              const fallbackName = loginSession.user.user_metadata?.full_name || loginSession.user.email?.split('@')[0] || null;
              if (fallbackName) {
                await supabase.from('profiles').update({ username: fallbackName, display_name: fallbackName }).eq('id', loginSession.user.id);
              }
            }
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) {
        const c = authErrorCopy(error, 'resend');
        console.error('[auth] resend failed:', c.detail);
        toast({ title: c.title, description: c.description, variant: 'destructive' });
      } else {
        toast({ title: 'Email resent', description: 'Check your inbox for the confirmation link.' });
      }
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        // Use the live origin (www on web, the preview host on previews), NOT a
        // hardcoded apex. The apex 301s to www and can drop the PKCE ?code= query
        // on the redirect, so the code never reaches /reset-password. Matches Login.tsx.
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        // The old fallback existed because error.message was sometimes the literal '{}'.
        // Classifying removes the need for that special case -- an unrecognised error now
        // gets deliberate generic copy rather than whatever the SDK happened to serialise.
        const c = authErrorCopy(error, 'reset');
        console.error('[auth] reset email failed:', c.detail);
        toast({ title: c.title, description: c.description, variant: 'destructive' });
      } else {
        setResetSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Biometric sign-in disabled — previously read a plaintext password from
  // unencrypted Capacitor Preferences. Will return with secure storage. See SAB-232.
  const handleBiometricSignIn = async () => {
    toast({
      title: 'Biometric sign-in temporarily disabled',
      description: 'We are upgrading biometric login to use secure storage. Please sign in with email and password.',
    });
  };

  if (suspendedUntil) {
    const suspendDate = new Date(suspendedUntil);
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border bg-card">
          <CardContent className="pt-8 pb-6 text-center space-y-5">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center">
                <Clock className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground font-display">Account Suspended</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Your account is suspended until{' '}
              <span className="text-foreground font-medium">
                {suspendDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>.
            </p>
            <p className="text-xs text-muted-foreground">
              You chose to take a break from Sabotage Fantasy. Your account will be automatically reactivated after this date.
            </p>
            <Button variant="outline" className="w-full" onClick={() => setSuspendedUntil(null)}>
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmationSent) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border bg-card">
          <CardContent className="pt-8 pb-6 text-center space-y-5">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground font-display">Check your email</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              We sent a confirmation link to{' '}
              <span className="text-foreground font-medium">{email}</span>.
              Click the link to activate your Sabotage Fantasy account.
            </p>
            <p className="text-xs text-muted-foreground">
              Didn't receive it? Check your spam folder or tap below to resend.
            </p>
            <Button variant="outline" className="w-full" onClick={handleResendEmail} disabled={resending}>
              {resending ? 'Sending...' : 'Resend Email'}
            </Button>
            <button
              type="button"
              onClick={() => { setConfirmationSent(false); setIsSignUp(false); }}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              Back to Sign In
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border bg-card">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-20 w-auto" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground font-display">
              {resetSent ? 'Check your email' : 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {resetSent
                ? 'We sent a reset link to ' + email
                : "Enter your email and we'll send you a reset link"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resetSent ? (
              <div className="text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
                <p className="text-sm text-foreground">Reset link sent. Check your inbox.</p>
                <p className="text-xs text-muted-foreground">The link expires in 1 hour. Didn't receive it? Check your spam folder.</p>
                <Button variant="outline" className="w-full" onClick={() => { setIsForgotPassword(false); setResetSent(false); }}>
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
                  ) : 'Send Reset Link'}
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => setIsForgotPassword(false)} className="text-sm text-primary hover:text-primary/80 transition-colors">
                    Back to Sign In
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark flex flex-col items-center justify-center px-4 gap-4">
      <Card className="w-full max-w-md border-border bg-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-20 w-auto" />
          </div>
          <p className="text-sm text-muted-foreground italic">Where Turnovers Are Touchdowns</p>
          <CardTitle className="text-2xl font-bold text-foreground font-display">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {isSignUp ? 'Sign up to start sabotaging' : 'Sign in to your league'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="username" className="text-foreground">Username</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                  <Input
                    id="username"
                    type="text"
                    placeholder="trashking99"
                    value={username}
                    onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    required
                    maxLength={20}
                    className="bg-input border-border text-foreground pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">3-20 characters, letters, numbers, underscores</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setPasswordMismatch(false); }}
                  required
                  minLength={isSignUp ? 10 : 6}
                  className="bg-input border-border text-foreground pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {isSignUp && (
                <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_TEXT}</p>
              )}
            </div>
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-foreground">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setPasswordMismatch(false); }}
                    required
                    minLength={10}
                    className={`bg-input border-border text-foreground pr-10 ${passwordMismatch ? 'border-destructive' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordMismatch && (
                  <p className="text-xs text-destructive">Passwords do not match.</p>
                )}
              </div>
            )}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="dob" className="text-foreground">Date of Birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={e => { setDateOfBirth(e.target.value); setEligibilityError(null); }}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="bg-input border-border text-foreground"
                />
                <p className="text-xs text-muted-foreground">
                  Must be 18+ (19+ in Nebraska).{' '}
                  <a href="/responsible-gaming" className="text-primary hover:underline">See eligibility</a>.
                </p>
              </div>
            )}
            {isSignUp && (
              <div className="space-y-2">
                <Label className="text-foreground flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  State of Residence
                </Label>
                <Select value={stateCode} onValueChange={(v) => { setStateCode(v); checkStateEligibility(v); }}>
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Select your state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {eligibilityError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-400">{eligibilityError}</p>
                  <p className="text-xs text-red-400/70 mt-1">
                    You can still browse the free demo at{' '}
                    <a href="/nfldemo" className="underline hover:text-red-300">sabotagefantasy.com/nfldemo</a>
                  </p>
                </div>
              </div>
            )}
            {!isSignUp && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}
            {authError && (
              <div role="alert" className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{authError}</p>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{isSignUp ? 'Creating account…' : 'Signing in…'}</>
              ) : isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          {!isSignUp && biometricAvailable && (
            <>
              <div className="flex items-center gap-3 mt-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <Button
                variant="outline"
                className="w-full mt-4 gap-2"
                onClick={handleBiometricSignIn}
                disabled={bioSigningIn}
              >
                <Fingerprint className="w-4 h-4" />
                {bioSigningIn ? 'Authenticating...' : `Sign in with ${biometricLabel}`}
              </Button>
            </>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => { setIsSignUp(!isSignUp); setPasswordMismatch(false); setConfirmPassword(''); }}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
          {isSignUp && (
            <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed">
              By creating an account you agree to our{' '}
              <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </p>
          )}
        </CardContent>
      </Card>

      <footer className="w-full max-w-md text-center text-[11px] text-muted-foreground pb-4">
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">T's &amp; C's</a>
          <span>·</span>
          <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Privacy</a>
          <span>·</span>
          <a href={LEGAL_LINKS.cookies} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Cookies</a>
          <span>·</span>
          <span>© {new Date().getFullYear()} Sabotage Fantasy LLC</span>
        </div>
      </footer>
    </div>
  );
};

export default Auth;
